/**
 * @fileoverview Virtual Race Shared Utilities
 * Centralized security, API patterns, storage, and rate limiting for all race files.
 */

(function() {
    'use strict';
    const CONFIG = {
        // API Configuration
        API_TIMEOUT: 10000,
        MAX_RETRIES: 3,
        RETRY_DELAY: 1000,
        BACKOFF_MULTIPLIER: 2,

        // Security Configuration
        MAX_REQUESTS_PER_MINUTE: 10,
        RATE_LIMIT_WINDOW_MS: 10000,
        STORAGE_KEY_PREFIX: 'vr_secure_',

        // Validation Configuration
        MIN_AGE: 13,
        MAX_AGE: 150,
        MAX_DISTANCE: 1000,
        MIN_DISTANCE: 0,
        DATE_RANGE_MONTHS: 3,
        MAX_COORDINATES: 10000,
        MAX_ACTIVITIES: 1000,
        MAX_TEAM_OPTIONS: 1000,

        // Time Configuration
        MAX_HOURS: 99,
        MAX_MINUTES: 59,
        MAX_SECONDS: 59,

        // Polling Configuration
        MAX_POLL_ATTEMPTS: 50,
        POLL_INTERVAL: 100,

        DEFAULT_COLOR: '#003B6E'
    };

    const SecurityUtils = {

        sanitizeText(text, maxLength = 1000) {
            if (!text || typeof text !== 'string') return '';
            return text
                .replace(/[<>]/g, '')
                .replace(/javascript:/gi, '')
                .replace(/on\w+=/gi, '')
                .trim()
                .substring(0, maxLength);
        },

        validateProductId(productId) {
            if (!productId) return null;
            const id = String(productId).trim();
            if (!/^[0-9]+$/.test(id) || id.length > 20) return null;
            const numId = parseInt(id, 10);
            if (isNaN(numId) || numId <= 0) return null;
            return id;
        },

        validateUrl(url) {
            if (!url) return '';
            try {
                const parsed = new URL(url);
                if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
                    return '';
                }
                return parsed.href;
            } catch (e) {
                return '';
            }
        },

        validateColor(color) {
            if (!color || typeof color !== 'string') return CONFIG.DEFAULT_COLOR;
            return /^#[0-9A-Fa-f]{6}$/.test(color) ? color : CONFIG.DEFAULT_COLOR;
        },

        sanitizeEmail(email) {
            if (!email || typeof email !== 'string') return '';
            return email.trim().toLowerCase().substring(0, 254);
        },

        sanitizeZipCode(zipCode) {
            if (!zipCode || typeof zipCode !== 'string') return '';
            return zipCode.trim().replace(/[^0-9-]/g, '').substring(0, 10);
        },

        sanitizeSearch(search) {
            return this.sanitizeText(search, 100);
        },

        escapeHtml(s) {
            if (s == null) return '';
            return String(s)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
        },

        validateTimeComponent(value, type) {
            const num = parseInt(value, 10);
            if (isNaN(num) || num < 0) return false;

            switch (type) {
                case 'hours':
                    return num >= 0 && num <= CONFIG.MAX_HOURS;
                case 'minutes':
                    return num >= 0 && num <= CONFIG.MAX_MINUTES;
                case 'seconds':
                    return num >= 0 && num <= CONFIG.MAX_SECONDS;
                default:
                    return false;
            }
        },

        validateAge(birthday) {
            if (!birthday) return false;

            try {
                const birthDate = new Date(birthday);
                if (isNaN(birthDate.getTime())) return false;

                const today = new Date();
                let age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                const dayDiff = today.getDate() - birthDate.getDate();
                if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
                    age--;
                }

                return age >= CONFIG.MIN_AGE && age <= CONFIG.MAX_AGE;
            } catch (error) {
                console.error('Age validation failed:', error.name);
                return false;
            }
        },

        validateDistance(distance) {
            const dist = parseFloat(distance);
            return !isNaN(dist) &&
                   dist >= CONFIG.MIN_DISTANCE &&
                   dist <= CONFIG.MAX_DISTANCE;
        },

        validateDate(dateValue) {
            if (!dateValue) return false;

            try {
                const selectedDate = new Date(dateValue);
                if (isNaN(selectedDate.getTime())) return false;

                selectedDate.setHours(0, 0, 0, 0);

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const threeMonthsAgo = new Date();
                threeMonthsAgo.setMonth(today.getMonth() - CONFIG.DATE_RANGE_MONTHS);

                if (selectedDate < threeMonthsAgo || selectedDate > today) {
                    return false;
                }

                return true;
            } catch (error) {
                console.error('Date validation failed:', error.name);
                return false;
            }
        },

        validateCoordinate(coordinate) {
            if (!coordinate || typeof coordinate !== 'object') return false;

            const lat = parseFloat(coordinate.latitude);
            const lng = parseFloat(coordinate.longitude);

            return !isNaN(lat) && !isNaN(lng) &&
                   lat >= -90 && lat <= 90 &&
                   lng >= -180 && lng <= 180;
        },

        validateActivity(activity) {
            if (!activity || typeof activity !== 'object') return false;
            return typeof activity.name === 'string' &&
                   typeof activity.time === 'string' &&
                   typeof activity.startDate === 'string';
        },

        validateDashboardData(data) {
            if (!data || typeof data !== 'object') return false;
            const response = data.response;
            if (response == null || typeof response !== 'object') return false;
            if (Array.isArray(response)) {
                if (response.length === 0) return true;
                const first = response[0];
                const coords = first && first.coordinates;
                if (coords != null && !Array.isArray(coords)) return false;
                if (Array.isArray(coords) && coords.length > CONFIG.MAX_COORDINATES) return false;
                return true;
            }
            if (!Array.isArray(response.coordinates)) return false;
            if (response.coordinates.length > CONFIG.MAX_COORDINATES) return false;
            return true;
        },

        validateActivityList(data) {
            if (!data || typeof data !== 'object') return false;
            if (!data.response || typeof data.response !== 'object') return false;
            if (!data.response.data || !Array.isArray(data.response.data)) return false;
            if (data.response.data.length > CONFIG.MAX_ACTIVITIES) return false;
            return true;
        },

        checkRateLimit(key) {
            const storageKey = `${CONFIG.STORAGE_KEY_PREFIX}rateLimit_${key}`;
            const now = Date.now();

            try {
                const stored = localStorage.getItem(storageKey);

                if (!stored) {
                    localStorage.setItem(storageKey, JSON.stringify({
                        count: 1,
                        resetAt: now + CONFIG.RATE_LIMIT_WINDOW_MS
                    }));
                    return { allowed: true, remainingTime: 0 };
                }

                const data = JSON.parse(stored);
                if (now > data.resetAt) {
                    localStorage.setItem(storageKey, JSON.stringify({
                        count: 1,
                        resetAt: now + CONFIG.RATE_LIMIT_WINDOW_MS
                    }));
                    return { allowed: true, remainingTime: 0 };
                }
                if (data.count >= CONFIG.MAX_REQUESTS_PER_MINUTE) {
                    return {
                        allowed: false,
                        remainingTime: Math.ceil((data.resetAt - now) / 1000)
                    };
                }
                data.count++;
                localStorage.setItem(storageKey, JSON.stringify(data));
                return { allowed: true, remainingTime: 0 };

            } catch (error) {
                console.error('Rate limit check failed:', error.name);
                // On error, allow request but clear corrupted data
                localStorage.removeItem(storageKey);
                return { allowed: true, remainingTime: 0 };
            }
        },

    };

    class StorageManager {

        static getSecure(key) {
            try {
                const storageKey = `${CONFIG.STORAGE_KEY_PREFIX}${key}`;
                const data = localStorage.getItem(storageKey);
                return data ? JSON.parse(data) : null;
            } catch (error) {
                console.error('Storage get failed:', error.name);
                return null;
            }
        }

        static setItem(key, value) {
            if (key == null || String(key).trim() === '') return;
            try {
                const storageKey = `${CONFIG.STORAGE_KEY_PREFIX}${key}`;
                const toStore = value !== undefined && value !== null ? JSON.stringify(value) : null;
                if (toStore !== null) {
                    localStorage.setItem(storageKey, toStore);
                } else {
                    localStorage.removeItem(storageKey);
                }
            } catch (error) {
                console.error('Storage setItem failed:', error.name);
            }
        }

        static getSelectedProduct() {
            return this.getSecure('selectedProduct');
        }

        static getSelectedProductId() {
            const v = this.getSelectedProduct();
            return v != null ? String(v) : '';
        }

        static setSelectedProductId(id) {
            if (id == null || String(id).trim() === '') return;
            this.setItem('selectedProduct', String(id).trim());
        }

        static getUserData() {
            return this.getSecure('userData');
        }

        static getSelectedActivity() {
            return this.getSecure('selectedActivity');
        }

        static getActivityType() {
            return this.getSecure('activityType');
        }
    }

    class TeamColorManager {
        constructor() {
            this._colorCache = new Map();
            this._colors = [
                '#2FA70C', '#457ADC', '#C21141', '#EC6600', '#23CEB7',
                '#FFC400', '#743FA8', '#E25EF1', '#FF0707', '#616161', '#9E9E9E'
            ];
            this._defaultColor = CONFIG.DEFAULT_COLOR;
            this._setDefaultCache();
        }

        _setDefaultCache() {
            this._colorCache.set('all', this._defaultColor);
            this._colorCache.set('all teams', this._defaultColor);
            this._colorCache.set('n/a', this._defaultColor);
        }

        getColorForTeam(teamName) {
            if (!teamName || typeof teamName !== 'string') {
                return this._defaultColor;
            }

            const normalized = SecurityUtils.sanitizeText(teamName).toLowerCase().trim();

            if (this._colorCache.has(normalized)) {
                return this._colorCache.get(normalized);
            }

            const color = this._hashToColor(normalized);
            this._colorCache.set(normalized, color);
            return color;
        }

        _hashToColor(str) {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                hash = str.charCodeAt(i) + ((hash << 5) - hash);
                hash = hash & hash;
            }
            const index = Math.abs(hash) % this._colors.length;
            return this._colors[index];
        }

        initializeFromTeams(teams) {
            if (!Array.isArray(teams)) {
                console.warn('Invalid teams data');
                return;
            }

            teams.forEach(team => {
                const teamName = team.teamName || team.key;
                if (teamName) {
                    const normalized = String(teamName).toLowerCase();
                    if (normalized !== 'n/a' && normalized !== 'all' && normalized !== 'all teams') {
                        this.getColorForTeam(teamName);
                    }
                }
            });
        }

        getAllColors() {
            const colors = {};
            this._colorCache.forEach((color, team) => {
                colors[team] = color;
            });
            return colors;
        }

        clearCache() {
            this._colorCache.clear();
            this._setDefaultCache();
        }

        static getTeamColorFromGlobal(teamName) {
            if (!teamName || typeof teamName !== 'string') {
                return CONFIG.DEFAULT_COLOR;
            }
            const normalized = SecurityUtils.sanitizeText(teamName).toLowerCase().trim();
            return window.teamColorDictionary?.[normalized] || CONFIG.DEFAULT_COLOR;
        }
    }

    class BaseAPIClient {
        constructor() {
            this._retryCount = 0;
            this._abortController = null;
        }

        async _executeWithRetry(operation, rateLimitKey = null) {
            if (rateLimitKey) {
                const rateLimit = SecurityUtils.checkRateLimit(rateLimitKey);
                if (!rateLimit.allowed) {
                    throw new Error(`Rate limit exceeded. Please wait ${rateLimit.remainingTime} seconds.`);
                }
            }

            this._retryCount = 0;

            while (this._retryCount < CONFIG.MAX_RETRIES) {
                try {
                    const result = await operation();
                    this._retryCount = 0;
                    return result;
                } catch (error) {
                    if (error.name === 'AbortError') {
                        throw error;
                    }
                    this._retryCount++;

                    if (this._retryCount >= CONFIG.MAX_RETRIES) {
                        console.error('Max retries reached:', error.name);
                        throw new Error('Failed to complete request after multiple attempts');
                    }
                    const delay = CONFIG.RETRY_DELAY * Math.pow(CONFIG.BACKOFF_MULTIPLIER, this._retryCount - 1);
                    console.error(`Retry ${this._retryCount}/${CONFIG.MAX_RETRIES} after ${delay}ms:`, error.name);

                    await this._delay(delay);
                }
            }
        }

        async _fetchWithTimeout(url, options = {}) {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), CONFIG.API_TIMEOUT);
            const externalSignal = options.signal;
            const onAbort = () => controller.abort();
            if (externalSignal) {
                externalSignal.addEventListener('abort', onAbort);
            }

            try {
                const response = await fetch(url, {
                    ...options,
                    credentials: options.credentials || 'same-origin',
                    signal: controller.signal
                });

                return response;
            } catch (error) {
                if (error.name === 'AbortError') {
                    throw error;
                }
                throw error;
            } finally {
                clearTimeout(timeoutId);
                if (externalSignal) {
                    externalSignal.removeEventListener('abort', onAbort);
                }
            }
        }

        _delay(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        }

        cancelPendingRequests() {
            if (this._abortController) {
                this._abortController.abort();
                this._abortController = null;
            }
        }
    }

    const DASHBOARD_CACHE_TTL_MS = 5 * 60 * 1000;
    const _dashboardCache = {};

    /**
     * Parses dashboard API response array to team options (single source for results + stats).
     * @param {*} response - data.response from dashboard API (array or single object)
     * @returns {Array<{key: string, value: string}>}
     */
    function parseDashboardResponseToTeamOptions(response) {
        if (!response || !Array.isArray(response)) return [];
        return response
            .filter(team =>
                team && team.teamName && String(team.teamName).toLowerCase() !== 'all teams'
            )
            .map(team => ({
                key: SecurityUtils.sanitizeText(team.teamName),
                value: SecurityUtils.sanitizeText(team.teamName)
            }));
    }

    /**
     * Fetches full dashboard data; uses cache to avoid duplicate requests (product + stats).
     * @param {string} productId - Valid product ID
     * @param {{ signal?: AbortSignal }} options - Optional AbortSignal for cancel (e.g. stats disconnect)
     * @returns {Promise<object>} Full dashboard API response
     */
    async function fetchDashboardData(productId, options = {}) {
        if (!SecurityUtils.validateProductId(productId)) {
            throw new Error('Invalid product ID');
        }
        const apiDomain = window.globalSpace?.apiDomain || '';
        if (!apiDomain) throw new Error('Missing API domain');

        const cached = _dashboardCache[productId];
        if (cached && (Date.now() - cached.at) < DASHBOARD_CACHE_TTL_MS) {
            return cached.data;
        }

        const client = new BaseAPIClient();
        const data = await client._executeWithRetry(async () => {
            const response = await client._fetchWithTimeout(
                `/apps/proxy/virtual-race/list/dashboard`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ product_id: String(productId) }),
                    signal: options.signal
                }
            );
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const text = await response.text();
            let parsed = null;
            try {
                parsed = text ? JSON.parse(text) : null;
            } catch (e) {
                throw new Error('Invalid JSON response');
            }
            if (!parsed || typeof parsed !== 'object') throw new Error('Invalid response body');
            const status = parsed.statusCode;
            if (status !== 200 && status !== '200') throw new Error('Invalid response status');
            if (!SecurityUtils.validateDashboardData(parsed)) throw new Error('Invalid dashboard data structure');
            return parsed;
        }, 'dashboardFetch');

        _dashboardCache[productId] = { data, at: Date.now() };
        return data;
    }

    /**
     * Fetches team list from Dashboard API (uses fetchDashboardData + cache).
     * @param {string} productId - Valid product ID
     * @returns {Promise<Array<{key: string, value: string}>>}
     */
    async function fetchDashboardTeamOptions(productId) {
        try {
            const data = await fetchDashboardData(productId);
            return parseDashboardResponseToTeamOptions(data && data.response);
        } catch (e) {
            console.warn('Dashboard teams fetch failed:', e?.message || e);
            return [];
        }
    }
    /**
     * Fetches the Race Team List section data from the Shopify storefront section endpoint.
     * @returns {Promise<any>} Parsed JSON data from the section (or null on error)
     */
    async function fetchRaceTeamListSection() {
        const url = '/?section_id=race_team_list';
        try {
            const response = await fetch(url, { method: 'GET', credentials: 'same-origin' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const html = await response.text();

            const div = document.createElement('div');
            div.innerHTML = html;

            const script = div.querySelector('script[type="application/json"][data-personalization-options]');
            if (script && script.textContent) {
                try {
                    const result = JSON.parse(script.textContent);
                    const allOptions = [];
                    const seenKeys = new Set();
                    for (const optionKey in result) {
                        if (Array.isArray(result[optionKey])) {
                            for (const option of result[optionKey]) {
                                if (
                                    option &&
                                    typeof option === 'object' &&
                                    'key' in option &&
                                    'label' in option
                                ) {
                                    const lowerKey = option.key.toLowerCase().replace(/\s+/g, '');
                                    if (!seenKeys.has(lowerKey)) {
                                        allOptions.push({
                                            key: option.key,
                                            label: option.label
                                        });
                                        seenKeys.add(lowerKey);
                                    }
                                }
                            }
                        }
                    }
                    return allOptions;
                } catch (e) {
                    console.warn('Failed to parse personalization JSON in section:', e);
                }
            }
            return null;
        } catch (e) {
            console.warn('Race team list section fetch failed:', e?.message || e);
            return null;
        }
    }

    const TeamLabelManager = {
        _cache: null,
        _fetchPromise: null,

        async _ensureTeamsLoaded() {
            if (this._cache) {
                console.log('TeamLabelManager: Using cached teams');
                return this._cache;
            }
            if (this._fetchPromise) {
                console.log('TeamLabelManager: Waiting for ongoing fetch');
                return this._fetchPromise;
            }

            console.log('TeamLabelManager: Fetching teams from server');
            this._fetchPromise = fetchRaceTeamListSection()
                .then(teams => {
                    this._cache = teams || [];
                    this._fetchPromise = null;
                    console.log('TeamLabelManager: Loaded teams', this._cache);
                    return this._cache;
                })
                .catch(err => {
                    console.warn('TeamLabelManager: Failed to load teams', err);
                    this._fetchPromise = null;
                    return [];
                });

            return this._fetchPromise;
        },

        async getTeamLabel(teamKey) {
            if (!teamKey || typeof teamKey !== 'string') return teamKey;
            
            const teams = await this._ensureTeamsLoaded();
            const normalizedKey = teamKey.toLowerCase().replace(/\s+/g, '');
            const matchedTeam = teams.find(team => 
                team.key && team.key.toLowerCase().replace(/\s+/g, '') === normalizedKey
            );
            
            return matchedTeam ? matchedTeam.label : teamKey;
        },

        getTeamLabelSync(teamKey) {
            if (!teamKey || typeof teamKey !== 'string') return teamKey;
            if (!this._cache) {
                console.warn('TeamLabelManager: Cache not loaded yet for', teamKey);
                return teamKey;
            }
            
            const normalizedKey = teamKey.toLowerCase().replace(/\s+/g, '');
            const matchedTeam = this._cache.find(team => 
                team.key && team.key.toLowerCase().replace(/\s+/g, '') === normalizedKey
            );
            
            return matchedTeam ? matchedTeam.label : teamKey;
        },

        clearCache() {
            this._cache = null;
            this._fetchPromise = null;
        }
    };


    const UIHelpers = {

        showAlert(element) {
            if (element) element.classList.remove('d-none');
        },

        hideAlert(element) {
            if (element) element.classList.add('d-none');
        },

        setFieldError(input, alert, hasError) {
            if (hasError) {
                this.showAlert(alert);
                if (input) input.classList.add('is-invalid');
            } else {
                this.hideAlert(alert);
                if (input) input.classList.remove('is-invalid');
            }
        }
    };

    const DOMHelpers = {

        show(element) {
            if (element) element.classList.remove('d-none');
        },

        hide(element) {
            if (element) element.classList.add('d-none');
        },

        toggle(element, condition) {
            if (element) {
                if (condition) {
                    element.classList.remove('d-none');
                } else {
                    element.classList.add('d-none');
                }
            }
        },

        _formatOptionLabel(str) {
            if (str == null || typeof str !== 'string') return '';
            return str.trim().split(/\s+/).map(word =>
                word.length ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''
            ).filter(Boolean).join(' ');
        },

        populateTeamDropdown(select, options, opts = {}) {
            console.log('options', options);
            if (!select || select.tagName !== 'SELECT') return;
            const defaultValue = opts.defaultValue !== undefined ? opts.defaultValue : 'all';
            const defaultLabel = opts.defaultLabel !== undefined ? opts.defaultLabel : 'ALL TEAMS';
            const selectedValue = opts.selectedValue;
            const getTeamColor = typeof opts.getTeamColor === 'function' ? opts.getTeamColor : null;

            const normalized = (options || []).map(opt => {
                if (opt.key != null) {
                    return { value: String(opt.key), label: String(opt.label != null ? opt.label : opt.key) };
                }
                if (opt.teamName != null || opt.ID != null) {
                    return {
                        value: String(opt.teamName || opt.ID),
                        label: String(opt.displayValue != null ? opt.displayValue : (opt.teamName || opt.ID))
                    };
                }
                return null;
            }).filter(Boolean);

            select.textContent = '';
            const defaultOpt = document.createElement('option');
            defaultOpt.value = defaultValue;
            defaultOpt.textContent = this._formatOptionLabel(defaultLabel);
            if (getTeamColor && defaultValue) {
                const color = defaultValue === 'all' ? CONFIG.DEFAULT_COLOR : getTeamColor(defaultValue);
                if (color) defaultOpt.dataset.color = color;
            }
            select.appendChild(defaultOpt);
            normalized.forEach(opt => {
                const o = document.createElement('option');
                o.value = opt.value;
                o.textContent = this._formatOptionLabel(opt.label);
                if (getTeamColor) {
                    const color = getTeamColor(opt.value);
                    if (color) o.dataset.color = color;
                }
                select.appendChild(o);
            });
            if (selectedValue !== undefined && selectedValue !== null) {
                select.value = selectedValue;
            }
        },

        /**
         * Applies team color to a select's background and border (shared by results + stats team dropdowns).
         * @param {HTMLSelectElement} select - Team select element
         * @param {string} selectedValue - Current value ('all' or team name)
         * @param {function(string): string} getTeamColor - (teamKey) => color
         * @param {string} [defaultColor] - Color for 'all' / empty (defaults to CONFIG.DEFAULT_COLOR)
         */
        applyTeamSelectColor(select, selectedValue, getTeamColor, defaultColor) {
            if (!select || select.tagName !== 'SELECT') return;
            const def = defaultColor != null ? defaultColor : CONFIG.DEFAULT_COLOR;
            const color = (!selectedValue || selectedValue === 'all')
                ? def
                : (typeof getTeamColor === 'function' ? getTeamColor(selectedValue) : null) || def;
            const safe = SecurityUtils.validateColor(color);
            select.style.backgroundColor = safe;
            select.style.borderColor = safe;
        },

        /**
         * Filters team options for dropdown display (excludes N/A and "all"). Shared by product + stats.
         * @param {Array<{key?: string, value?: string, teamName?: string}>} [teamOptions]
         * @returns {Promise<Array>}
         */
        async filterTeamOptionsForDropdown(teamOptions) {
            if (!Array.isArray(teamOptions)) return [];
            
            const allTeams = await TeamLabelManager._ensureTeamsLoaded();
            if (!allTeams || !Array.isArray(allTeams)) {
                return [];
            }

            const filteredTeams = new Set();
            const result = [];

            for (const option of teamOptions) {
                const optionKey = option && (option.key != null ? option.key : option.teamName);
                if (!optionKey || typeof optionKey !== 'string') continue;

                const normalizedKey = optionKey.toLowerCase().replace(/\s+/g, '');
                if (normalizedKey === 'n/a' || normalizedKey === 'all') continue;

                if (filteredTeams.has(normalizedKey)) continue;

                const matchingTeam = allTeams.find(team => 
                    team.key && team.key.toLowerCase().replace(/\s+/g, '') === normalizedKey
                );

                filteredTeams.add(normalizedKey);
                
                if (matchingTeam) {
                    result.push({
                        key: matchingTeam.key,
                        label: matchingTeam.label,
                        teamName: matchingTeam.label
                    });
                } else {
                    result.push({
                        key: optionKey,
                        label: optionKey,
                        teamName: optionKey
                    });
                }
            }

            return result;
        }
    };

    const EventHelpers = {

        dispatch(eventName, detail, target = document) {
            if (target && typeof eventName === 'string') {
                target.dispatchEvent(new CustomEvent(eventName, { detail }));
            }
        }
    };

    const DateHelpers = {

        toYMD(dateValue) {
            if (!dateValue) return '';
            try {
                return new Date(dateValue).toISOString().split('T')[0];
            } catch (e) {
                console.error('Date formatting failed:', e.name);
                return '';
            }
        },

    };

    const TimeBasedGreeting = {

        getGreeting() {
            const hour = new Date().getHours();
            if (hour >= 5 && hour < 12) return 'Good morning';
            if (hour >= 12 && hour < 17) return 'Good afternoon';
            if (hour >= 17 && hour < 22) return 'Good evening';
            return 'Good night';
        }
    };

    const UrlParams = {

        get(name) {
            if (!name || typeof name !== 'string') return '';
            return Utility.getUrlParameter(name);
        },

        getVrId() {
            const raw = this.get('vrId').trim();
            if (!raw) return null;
            return SecurityUtils.validateProductId(raw) || null;
        },

        getProductIdFromElement(element) {
            const fromAttr = element && element.getAttribute && (element.getAttribute('product-id') || '').trim();
            if (fromAttr && SecurityUtils.validateProductId(fromAttr)) return fromAttr;
            return this.getVrId();
        }
    };

    class BaseCustomElement extends HTMLElement {
        constructor() {
            super();
            this._state = null;
            this._isInitializing = true;
            this._hasError = false;
            this._errorMessage = '';
            this._eventListeners = [];
        }

        connectedCallback() {
            this._renderLoadingState();

            this._init()
                .then(async () => {
                    this._isInitializing = false;
                    this._hasError = false;
                    await this._render();
                })
                .catch((error) => {
                    console.error(`${this.constructor.name} initialization failed:`, error);
                    this._isInitializing = false;
                    this._hasError = true;
                    this._errorMessage = this._getUserFriendlyError(error);
                    this._renderErrorState();
                });
        }

        async _init() {
            throw new Error(`${this.constructor.name} must implement _init() method`);
        }

        _render() {
        }

        _renderLoadingState() {
            this._toggleElements('[data-loading]', true);
            this._toggleElements('[data-content]', false);
            this._toggleElements('[data-error]', false);
        }

        _renderErrorState() {
            this._toggleElements('[data-loading]', false);
            this._toggleElements('[data-content]', false);
            this._toggleElements('[data-error]', true);

            const errorContainer = this._$('[data-error-message]');
            if (errorContainer) {
                errorContainer.textContent = this._errorMessage;
            }
            const retryBtn = this._$('[data-error-retry]');
            if (retryBtn) {
                const handleRetry = () => {
                    this._hasError = false;
                    this._isInitializing = true;
                    this.connectedCallback();
                };
                this._addEventListener(retryBtn, 'click', handleRetry);
            }
        }

        _getUserFriendlyError(error) {
            if (!error) return 'An unknown error occurred.';

            if (error.name === 'AbortError') {
                return 'Request timed out. Please check your connection and try again.';
            }

            const msg = error.message || '';

            if (msg.includes('HTTP') || msg.includes('fetch failed')) {
                return 'Unable to load data. Please try again later.';
            }

            if (msg.includes('JSON') || msg.includes('Invalid response')) {
                return 'Received invalid data. Please contact support if this continues.';
            }

            if (msg.includes('vrId') || msg.includes('product') || msg.includes('required')) {
                return 'Missing required information. Please check the URL.';
            }

            return 'Something went wrong. Please try again.';
        }

        _toggleEl(el, show) {
            if (!el) return;
            el.hidden = !show;
            if (show) el.classList.remove('hidden');
            else el.classList.add('hidden');
        }

        _toggleElements(selector, show) {
            this._$$(selector).forEach(el => this._toggleEl(el, show));
        }

        _addEventListener(element, event, handler, options) {
            if (!element) return;
            element.addEventListener(event, handler, options);
            this._eventListeners.push({ element, event, handler, options });
        }

        disconnectedCallback() {
            this._eventListeners.forEach(({ element, event, handler, options }) => {
                element.removeEventListener(event, handler, options);
            });
            this._eventListeners = [];
            if (this._state && typeof this._state.destroy === 'function') {
                this._state.destroy();
            }
            this._state = null;
            if (typeof this._cleanup === 'function') {
                this._cleanup();
            }
        }

        _getProductId() {
            const productId = UrlParams.getProductIdFromElement(this);
            if (!productId) {
                throw new Error(`${this.constructor.name}: vrId query param required (e.g. ?vrId=123)`);
            }
            return productId;
        }

        _$(selector) {
            return this.querySelector(selector);
        }

        _$$(selector) {
            return Array.from(this.querySelectorAll(selector));
        }

        _setText(selector, text) {
            const el = this._$(selector);
            if (el) el.textContent = text || '';
        }

        _setHTML(selector, html) {
            const el = this._$(selector);
            if (el) el.innerHTML = html || '';
        }

        _toggle(selector, show) {
            this._toggleEl(this._$(selector), show);
        }
    }

    window.VirtualRaceUtils = {
        CONFIG,
        SecurityUtils,
        StorageManager,
        TeamColorManager,
        TeamLabelManager,
        BaseAPIClient,
        parseDashboardResponseToTeamOptions,
        fetchDashboardData,
        fetchDashboardTeamOptions,
        UIHelpers,
        DOMHelpers,
        EventHelpers,
        DateHelpers,
        TimeBasedGreeting,
        UrlParams,
        BaseCustomElement,
        fetchRaceTeamListSection,
    };
})();
